import { Builder } from '@builder.io/react'
import { Cache } from 'aws-amplify'
import CdnImage from 'components/util/CdnImage'
import { Formik, useFormikContext } from 'formik'
import FormikErrorFocus from 'formik-error-focus'
import { navigate } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { parse } from 'query-string'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Select, { components } from 'react-select'
import { updateCouponCodeValidity } from 'state/actions/dogAction'
import Api from 'state/apis/api'
import Lock from '../../../images/builder/Lock.svg'
import loadingGIF from '../../../images/loadingspinner.gif'
import { LinkButtonProp, LinkButtonRegisterProps } from '../common/button'
import { ageBrackets, calculatedData, weightBrackets } from './data'

enum Alignment {
  Center = 'center',
  Left = 'left',
  Right = 'right'
}
interface Props {
  backgroundColor: string
  backgroundImage: any
  backgroundImageMobile: any
  backgroundImageAlt: string
  titleOne: string
  titleColor: string
  titleTwo: string
  textAlignment: Alignment
  buttonAlignment: Alignment
  buttonProp: LinkButtonProp
  buttonEnabled: boolean
  showWidget: boolean
  widgetBackgroundColor: string
  widgetType: WidgetType
  widgetTags: string
  heroFooterBackground: string
  defaultOffer: boolean
}

const HeroCalculator = ({
  backgroundColor,
  backgroundImage,
  backgroundImageMobile,
  backgroundImageAlt,
  titleOne,
  titleColor,
  titleTwo,
  textAlignment,
  buttonAlignment,
  buttonProp,
  buttonEnabled,
  showWidget,
  widgetBackgroundColor,
  widgetType,
  widgetTags,
  heroFooterBackground,
  defaultOffer
}: Props) => {
  const {
    lastValidatedCoupons
  }: {
    lastValidatedCoupons: CouponsType[]
    loading: boolean
  } = useSelector((state: RootState) => state.dog)
  const formikRef = React.useRef<any>()
  const [isMobile, setIsMobile] = useState(false)
  const [sortedBreeds, setSortedBreeds] = useState([])
  const [selectedAge, setSelectedAge] = useState<number | null>(null)
  const [selectedWeight, setSelectedWeight] = useState<number | null>(null)
  const [selectedBreed, setSelectedBreed] = useState('')
  const [updatePrices, setUpdatePrices] = useState(false)

  const dispatch = useDispatch()

  useEffect(() => {
    const trustWidget = document.getElementById('trust-pilot-micro-combo')
    window.Trustpilot?.loadFromElement(trustWidget)
  }, [widgetType])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    callBreeds()
  }, [])

  const callBreeds = () => {
    const url = '/v1/public/signup/breeds'
    Api.get('wwwREST', url, {})
      .then(({ breedsList }) => {
        setSortedBreeds(breedsList)
      })
      .catch(err => {
        console.log(err)
      })
  }

  useEffect(() => {
    // Flash the loading on breed select
    // to give feedback to the user
    if (selectedBreed && selectedAge && selectedWeight) {
      setUpdatePrices(true)
      const timer = setTimeout(() => {
        setUpdatePrices(false)
      }, 200)

      return () => clearTimeout(timer)
    }
  }, [selectedBreed])

  // Handle coupons from the URL querystring
  useEffect(() => {
    // Priorise the QS sign up flow coupon and update store
    if (!defaultOffer) {
      const { coupon: couponFromQueryStr } = parse(location.search)
      let coupon = couponFromQueryStr ?? Cache.getItem('coupon') ?? null
      if (coupon) {
        coupon = coupon.toUpperCase()
        dispatch(updateCouponCodeValidity('checkValidity', coupon as string))
      }
    }
  }, [])

  const getAppliedDiscount = (price: number) => {
    if (lastValidatedCoupons && lastValidatedCoupons[0]?.type === 'percentage' && lastValidatedCoupons[0]?.amount) {
      const discountAmount = (price * lastValidatedCoupons[0].amount) / 100
      return price - discountAmount
    }
    return price
  }

  const getPrice = (type: 'full' | 'partial', applyDiscount = false) => {
    if (selectedAge && selectedWeight) {
      const price = calculatedData[selectedAge][selectedWeight][type]
      return applyDiscount ? getAppliedDiscount(price) : price
    }
    return 0
  }

  const DropdownIndicator = (props: ElementConfig<typeof components.DropdownIndicator>) => {
    return (
      <components.DropdownIndicator {...props}>
        <StaticImage className="w-5 h-full" src="../../../images/checkout/cheveronDown.svg" alt="Dropdown" />
      </components.DropdownIndicator>
    )
  }

  const getAlignmentClass = () => {
    if (textAlignment === Alignment.Right) {
      return 'text-right'
    }
    if (textAlignment === Alignment.Center) {
      return 'text-center'
    }
    return 'text-left'
  }

  const getJustifyAlignmentClass = () => {
    if (buttonAlignment === Alignment.Right) {
      return 'ml-auto'
    }
    if (buttonAlignment === Alignment.Center) {
      return 'mx-auto'
    }
    return 'mr-auto lg:ml-16'
  }

  const triggerTagging = (eventName: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, {
        page: 'home_page'
      })
    }
  }

  const renderMobileBanner = () => {
    return (
      <>
        {backgroundImageMobile && (
          <CdnImage
            className={`index-2 relative h-full w-full object-cover min-h-[11rem] lg:hidden`}
            url={backgroundImageMobile}
            alt={backgroundImageAlt}
            steps={[600, 1080]}
            width="auto"
            lazyLoad={false}
          />
        )}
      </>
    )
  }

  const renderHeroText = () => {
    return (
      <div className={`text-center lg:${getAlignmentClass()} pb-2 lg:pb-4`}>
        <span className="text-xl lg:text-4xl font-medium font-serif" style={{ color: titleColor }}>
          {titleOne}
        </span>
        <br />
        <span className="text-xl lg:text-4xl font-medium font-serif" style={{ color: titleColor }}>
          {titleTwo}
        </span>
        <div />
      </div>
    )
  }

  const renderCTAButton = () => {
    if (buttonEnabled) {
      return (
        <button
          className={`flex w-11/12 items-center justify-center mx-auto lg:${getJustifyAlignmentClass()} rounded-full border border-transparent bg-[#006685] px-5 py-3 text-base font-medium text-white md:w-96 md:text-lg`}
          onClick={() => {
            if (typeof window.gtag !== 'undefined') {
              window.gtag('event', 'cta_component_dog_hero_calculator_cta_logged_in', {
                page: 'home_page'
              })
            }

            if (selectedBreed && typeof selectedBreed === 'string' && selectedBreed.trim() !== '') {
              let url = `/signup/dog-details/?b=${encodeURI(btoa(selectedBreed))}`
              window.location.href = url
            }
            navigate(`${buttonProp?.buttonUrl}`)
          }}
        >
          {`${buttonProp?.buttonText}`}
        </button>
      )
    }

    return null
  }

  const getWidget = () => {
    if (showWidget) {
      return (
        <div className="flex justify-center lg:justify-start pt-8">
          {/* <!-- TrustBox widget - Micro Combo --> */}
          <div
            className="trustpilot-widget flex justify-center lg:justify-start"
            data-locale="en-GB"
            data-template-id="5419b6ffb0d04a076446a9af"
            data-businessunit-id="5af423d4a3303b0001bf47c1"
            data-style-height="20px"
            data-style-width="100%"
            data-theme="light"
            data-stars="5"
            data-font-family="Montserrat"
            style={{ backgroundColor: widgetBackgroundColor }}
            data-text-color="#004355"
            data-tags={widgetTags ? widgetTags : ''}
            id="trust-pilot-micro-combo"
          >
            <a href="https://uk.trustpilot.com/review/differentdog.com" target="_blank" rel="noreferrer noopener">
              <StaticImage
                objectFit="contain"
                className="w-full "
                src="../../../images/trustpilot.png"
                alt="Trustpilot"
              />
            </a>
          </div>
          {/* <!-- End TrustBox widget --> */}
        </div>
      )
    }
  }

  const CustomSingleValue = ({ children, ...props }: any) => (
    <components.SingleValue {...props} key={props.selectProps.placeholder}>
      <div className="flex flex-col justify-center items-center h-8 -mr-6">
        <span className="text-sm text-[#004355] leading-4">{props.selectProps.placeholder}</span>
        <span className="leading-4">{children}</span>
      </div>
    </components.SingleValue>
  )

  const renderCalculator = () => {
    return (
      <Formik
        innerRef={formikRef}
        initialValues={{
          age: '',
          weight: '',
          breed: ''
        }}
        validateOnMount={false}
      >
        {props => {
          return (
            <>
              <FormikErrorFocus
                formik={useFormikContext()}
                offset={0}
                align={'middle'}
                focusDelay={1000}
                ease={'linear'}
                duration={1000}
              />

              <div className={`mx-auto mt-4 w-5/6 rounded-3xl bg-white py-4 px-6`}>
                <span className="text-sm md:text-base font-medium mb-2 md:mb-4 flex justify-center text-center font-sans lg:text-lg">
                  Answer three quick questions so we can give you an estimated price for your plan.
                </span>
                <div className="flex flex-col lg:flex-row">
                  <div className="my-2 lg:my-4 flex w-full items-center justify-center">
                    {/* Age */}
                    <Select
                      className="block w-11/12 self-center rounded-full border-2 border-secondary bg-white py-2 px-4 text-center focus:border-highlight focus:ring-highlight mx-auto z-50"
                      theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                          ...theme.colors,
                          primary25: '#F5F1ED',
                          primary: '#6BA2B3',
                          primary50: '#6BA2B3'
                        }
                      })}
                      styles={{
                        control: (base, _state) => ({
                          ...base,
                          zIndex: 9999,
                          border: 'none',
                          boxShadow: 'none !important',
                          '*': {
                            boxShadow: 'none !important'
                          },
                          '&:hover': {
                            border: 'none !important'
                          }
                        }),
                        valueContainer: (provided, _state) => ({
                          ...provided,
                          padding: '0',
                          borderRadius: '30px'
                        }),
                        placeholder: (provided, _state) => ({
                          ...provided,
                          marginLeft: '1.5rem'
                        }),
                        menu: provided => ({
                          ...provided,
                          borderRadius: '10px',
                          margin: '0 auto',
                          width: '92%',
                          justifyContent: 'center'
                        }),
                        dropdownIndicator: base => ({
                          ...base,
                          color: '#006685'
                        })
                      }}
                      components={{
                        IndicatorSeparator: () => null,
                        DropdownIndicator,
                        SingleValue: CustomSingleValue
                      }}
                      placeholder={'Age'}
                      onChange={(e: any) => {
                        triggerTagging('dog_hero_calculator_age_selected')
                        setSelectedAge(Number(e.value))
                      }}
                      options={ageBrackets}
                      isSearchable={false}
                      isClearable={false}
                      onBlur={(e: any) => {
                        if (e.value) {
                          triggerTagging('dog_hero_calculator_age_selected')

                          setSelectedAge(Number(e.value))
                        }
                      }}
                    />
                  </div>
                  <div className="my-2 lg:my-4 flex w-full items-center justify-center">
                    {/* Weight */}
                    <Select
                      className="block w-11/12 self-center rounded-full border-2 border-secondary bg-white py-2 px-4 text-center focus:border-highlight focus:ring-highlight mx-auto z-40"
                      theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                          ...theme.colors,
                          primary25: '#F5F1ED',
                          primary: '#6BA2B3',
                          primary50: '#6BA2B3'
                        }
                      })}
                      styles={{
                        control: (base, _state) => ({
                          ...base,
                          zIndex: 9999,
                          border: 'none',
                          boxShadow: 'none !important',
                          '*': {
                            boxShadow: 'none !important'
                          },
                          '&:hover': {
                            border: 'none !important'
                          }
                        }),
                        valueContainer: (provided, _state) => ({
                          ...provided,
                          padding: '0',
                          borderRadius: '30px'
                        }),
                        placeholder: (provided, _state) => ({
                          ...provided,
                          marginLeft: '1.5rem'
                        }),
                        menu: provided => ({
                          ...provided,
                          borderRadius: '10px',
                          margin: '0 auto',
                          width: '92%',
                          justifyContent: 'center'
                        }),
                        dropdownIndicator: base => ({
                          ...base,
                          color: '#006685'
                        })
                      }}
                      components={{
                        IndicatorSeparator: () => null,
                        DropdownIndicator,
                        SingleValue: CustomSingleValue
                      }}
                      placeholder={'Weight'}
                      onChange={(e: any) => {
                        triggerTagging('dog_hero_calculator_weight_selected')
                        setSelectedWeight(e.value)
                      }}
                      options={weightBrackets}
                      isClearable={false}
                      isSearchable={false}
                      onBlur={(e: any) => {
                        if (e.value) {
                          triggerTagging('dog_hero_calculator_weight_selected')
                          setSelectedWeight(e.value)
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="my-2 lg:my-4 flex w-full items-center justify-center">
                  {/* Breed */}
                  <Select
                    className="block w-11/12 lg:w-[97%] self-center rounded-full border-2 border-secondary bg-white py-2 px-4 text-center focus:border-highlight focus:ring-highlight mx-auto z-30"
                    theme={theme => ({
                      ...theme,
                      borderRadius: 0,
                      colors: {
                        ...theme.colors,
                        primary25: '#F5F1ED',
                        primary: '#6BA2B3',
                        primary50: '#6BA2B3'
                      }
                    })}
                    styles={{
                      control: (base, _state) => ({
                        ...base,
                        zIndex: 9999,
                        border: 'none',
                        boxShadow: 'none !important',
                        '*': {
                          boxShadow: 'none !important'
                        },
                        '&:hover': {
                          border: 'none !important'
                        }
                      }),
                      valueContainer: (provided, _state) => ({
                        ...provided,
                        padding: '0',
                        borderRadius: '30px'
                      }),
                      placeholder: (provided, _state) => ({
                        ...provided,
                        marginLeft: '1.5rem'
                      }),
                      menu: provided => ({
                        ...provided,
                        borderRadius: '10px',
                        margin: '0 auto',
                        width: '92%',
                        justifyContent: 'center'
                      }),
                      dropdownIndicator: base => ({
                        ...base,
                        color: '#006685'
                      })
                    }}
                    components={{
                      IndicatorSeparator: () => null,
                      DropdownIndicator,
                      SingleValue: CustomSingleValue
                    }}
                    placeholder={'Breed'}
                    onChange={(e: any) => {
                      triggerTagging('dog_hero_calculator_breed_selected')
                      setSelectedBreed(e.value)
                    }}
                    options={sortedBreeds}
                    isClearable={false}
                    onBlur={(e: any) => {
                      if (e.value) {
                        triggerTagging('dog_hero_calculator_breed_selected')
                        setSelectedBreed(e.value)
                      }
                    }}
                  />
                </div>

                {/* Price box */}
                <div className="flex flex-row mx-auto mt-4 w-[97%] rounded-xl p-4 bg-[#F9F5F1] justify-center items-center relative">
                  {/* Full Diet Section */}
                  {(selectedAge === null || selectedWeight == null || getPrice('full') !== 0) && (
                    <div className="flex w-full justify-center rounded-3xl ">
                      <div className="flex flex-col w-full">
                        <div className="font-serif font-medium text-lg text-center">Full diet</div>
                        <div className="flex flex-col lg:flex-row mt-2 w-full">
                          <div className="flex items-center justify-center w-full lg:w-1/2">
                            <StaticImage
                              className={`${getPrice('partial') !== 0 ? 'w-2/3' : 'w-1/3'}`}
                              alt="Full Diet"
                              src="../../../../images/full-diet-bowl.png"
                            />
                          </div>

                          <div className="flex flex-col justify-center items-center lg:items-start mt-2 lg:mt-0 w-full lg:w-1/2">
                            <p className="font-sans font-normal text-base">From:</p>
                            <span className="font-bold">
                              <span className="font-sans font-bold text-[#628F5B] text-xl">
                                &pound;{(getPrice('full', true) / 100)?.toFixed(2)}
                              </span>{' '}
                              per day
                            </span>
                            <span className="line-through font-normal text-sm text-[#5F6977]">
                              &pound;{(getPrice('full') / 100)?.toFixed(2)} per day
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Partial Diet Section */}
                  {(selectedAge === null || selectedWeight == null || getPrice('partial') !== 0) && (
                    <div className="flex w-full justify-center rounded-3xl">
                      <div className="flex flex-col w-full">
                        <div className="font-serif font-medium text-lg text-center">Partial diet</div>
                        <div className="flex flex-col lg:flex-row mt-2 w-full">
                          <div className="flex items-center justify-center w-full lg:w-1/2">
                            <StaticImage
                              className={`${getPrice('full') !== 0 ? 'w-2/3' : 'w-1/3'}`}
                              alt="Partial Diet"
                              src="../../../../images/partial-diet-bowl.png"
                            />
                          </div>

                          <div className="flex flex-col justify-center items-center lg:items-start mt-2 lg:mt-0 w-full lg:w-1/2">
                            <p className="font-sans font-normal text-base">From:</p>
                            <span className="font-bold">
                              <span className="font-sans font-bold text-[#628F5B] text-xl">
                                &pound;{(getPrice('partial', true) / 100)?.toFixed(2)}
                              </span>{' '}
                              per day
                            </span>
                            <span className="line-through font-normal text-sm text-[#5F6977]">
                              &pound;{(getPrice('partial') / 100)?.toFixed(2)} per day
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Unlock overlay message */}
                  {!selectedAge && (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center backdrop-blur-md rounded-3xl px-2">
                      <div className="flex items-center justify-center text-left">
                        <img className="block pl-2 lg:pl-0" src={Lock} alt="Lock" />
                        <span className="text-sm font-sans font-semibold bg-opacity-70 p-4 rounded-lg">
                          Unlock your personalised
                          <br /> fresh food subscription today
                        </span>
                      </div>
                    </div>
                  )}
                  {((selectedAge && !selectedWeight) || updatePrices) && (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center backdrop-blur-md rounded-3xl">
                      <div className="flex items-center justify-center text-left">
                        <img className="m-auto" width="80px" src={loadingGIF} alt="Loading, please wait..." />
                        <span className="text-sm font-sans font-semibold bg-opacity-70 p-4 -ml-6 rounded-lg">
                          Calculating your plan prices
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )
        }}
      </Formik>
    )
  }

  return (
    <>
      <div className="bg-image-none relative mb-0 h-full w-full md:bg-cover md:bg-bottom md:bg-no-repeat">
        {backgroundImage && (
          <CdnImage
            className="absolute top-0 left-0 h-full w-full object-cover object-bottom aspect-auto max-h-full max-w-full bg-transparent text-white xxxl:object-center hidden lg:block lg:min-h-[55rem]"
            url={backgroundImage}
            alt={backgroundImageAlt}
            lazyLoad={true}
            style={{
              dpr: 'auto'
            }}
          />
        )}
        <main className="relative z-10 mx-auto flex h-full w-full flex-col lg:flex-row 2xl:container">
          {/* Left section */}
          <div className="w-1/2 flex justify-start" />
          {/* Mobile image */}
          <div className="block lg:hidden">{renderMobileBanner()}</div>
          {/* Right section */}
          <div
            className={`mx-auto h-full w-full lg:max-h-[1000px] lg:min-h-[24rem] lg:w-1/2 flex flex-col justify-center -mt-6 lg:mt-0 py-12 lg:pt-20 xl:pb-20 lg:mr-20`}
            style={{ backgroundColor: isMobile ? backgroundColor : 'transparent' }}
          >
            {renderHeroText()}
            {renderCalculator()}
            <div className="my-2 mx-auto mt-10 w-full">{renderCTAButton()}</div>
            <div className="w-full center">{getWidget()}</div>
          </div>
        </main>

        {/* Desktop footer */}
        <div
          className="relative w-full bg-cover bg-top bg-no-repeat lg:h-20 xl:mb-8 xl:-mt-4 2xl:h-24 lg:-mt-[3.5rem] hidden lg:block"
          style={{
            backgroundColor: '#FFFFFF',
            backgroundImage: heroFooterBackground ? `url(${heroFooterBackground})` : 'none'
          }}
        />
        {/* Mobile footer */}
        <div
          className="relative w-full bg-cover bg-top bg-no-repeat h-12 mb:4 md:mb-4 -mt-2 md:-mt-4 block lg:hidden"
          style={{
            backgroundColor: '#FFFFFF',
            backgroundImage: heroFooterBackground ? `url(${heroFooterBackground})` : 'none'
          }}
        />
      </div>
    </>
  )
}

export default HeroCalculator

Builder.registerComponent(HeroCalculator, {
  name: 'HeroCalculator',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'backgroundImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'backgroundImageMobile',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'backgroundImageAlt',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'titleOne',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'titleColor',
      type: 'color',
      defaultValue: '#006685'
    },
    {
      name: 'titleTwo',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center'
    },
    {
      name: 'showWidget',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'widgetBackgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'widgetType',
      type: 'string',
      enum: ['carousel', 'microCombo', 'list'],
      defaultValue: 'microCombo'
    },
    {
      name: 'widgetTags',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'buttonEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      ...LinkButtonRegisterProps,
      name: 'buttonProp'
    },
    {
      name: 'buttonAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center'
    },
    {
      name: 'heroFooterBackground',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'heroFooterBackgroundMobile',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'defaultOffer',
      type: 'boolean',
      defaultValue: false
    }
  ]
})
